import * as THREE from 'three';
import { latLonToPoint3D } from './geodata';
import type { CountryData } from './countries';
import vertexShader from './shaders/countryGlow.vert.glsl?raw';
import fragmentShader from './shaders/countryGlow.frag.glsl?raw';

const GLOW_BASE_RADIUS = 10.00;
const NUM_LAYERS = 10;   // line loops stacked above the surface
const LAYER_SPACING = 0.01; // units between each layer

type TrackedCountry = { data: CountryData; guessTime: number };

export class CountryHighlighter extends THREE.Group {
  private readonly _countries: TrackedCountry[] = [];
  private readonly _addedIds: Set<string> = new Set();
  private readonly _colorMap: Map<string, THREE.Color> = new Map();
  private _lines: THREE.LineSegments;
  private readonly _shaderMaterial: THREE.ShaderMaterial;
  private _currentTime = 0;

  constructor() {
    super();

    this._shaderMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
      },
      vertexShader,
      fragmentShader,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    });

    this._lines = new THREE.LineSegments(new THREE.BufferGeometry(), this._shaderMaterial);
    this.add(this._lines);
  }

  public addCountries(countries: CountryData[], colorMap: Map<string, THREE.Color>): void {
    let changed = false;
    for (const country of countries) {
      if (!this._addedIds.has(country.id)) {
        this._addedIds.add(country.id);
        this._countries.push({ data: country, guessTime: this._currentTime });
        const color = colorMap.get(country.id);
        if (color) this._colorMap.set(country.id, color);
        changed = true;
      }
    }
    if (changed) this.rebuildLines();
  }

  public update(elapsedTime: number): void {
    this._currentTime = elapsedTime;
    this._shaderMaterial.uniforms.time.value = elapsedTime;
  }

  private rebuildLines(): void {
    const positions: number[] = [];
    const colors: number[] = [];
    const layerNorms: number[] = [];
    const guessTimes: number[] = [];
    const indices: number[] = [];
    let vi = 0;

    const addRing = (
      coords: GeoJSON.Position[],
      color: THREE.Color,
      guessTime: number,
      layerNorm: number,
      radius: number,
    ): void => {
      const start = vi;
      for (const coord of coords) {
        const pt = latLonToPoint3D(coord[1], coord[0], radius);
        positions.push(pt.x, pt.y, pt.z);
        colors.push(color.r, color.g, color.b);
        guessTimes.push(guessTime);
        layerNorms.push(layerNorm);
        vi++;
      }
      const count = vi - start;
      for (let i = 0; i < count - 1; i++) {
        indices.push(start + i, start + i + 1);
      }
      indices.push(start + count - 1, start); // close the ring
    };

    for (const { data: country, guessTime } of this._countries) {
      const color = this._colorMap.get(country.id) ?? new THREE.Color(1, 1, 1);
      for (let layer = 0; layer < NUM_LAYERS; layer++) {
        const layerNorm = layer / (NUM_LAYERS - 1);
        const radius = GLOW_BASE_RADIUS + layer * LAYER_SPACING;
        if (country.geometry.type === 'Polygon') {
          addRing(country.geometry.coordinates[0], color, guessTime, layerNorm, radius);
        } else if (country.geometry.type === 'MultiPolygon') {
          for (const polygon of country.geometry.coordinates) {
            addRing(polygon[0], color, guessTime, layerNorm, radius);
          }
        }
      }
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.setAttribute('layerNorm', new THREE.Float32BufferAttribute(layerNorms, 1));
    geo.setAttribute('guessTime', new THREE.Float32BufferAttribute(guessTimes, 1));
    geo.setIndex(indices);

    this._lines.geometry.dispose();
    this._lines.geometry = geo;
  }
}
